import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../lib/api';
import { CheckCircle2, Clock, FileCheck } from 'lucide-react';

export default function ClienteVerification() {
  const { cedula } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const checkStatus = useCallback(async () => {
    try {
      const response = await api.get(`/cliente/${cedula}/verification-status`);
      const data = response.data;

      if (data.status === 'aprobado') {
        navigate(`/cliente/${cedula}/success`, { replace: true });
        return;
      }
    } catch (error) {
      console.error('Error verificando estado:', error);
    } finally {
      setLoading(false);
    }
  }, [cedula, navigate]);

  useEffect(() => {
    checkStatus();
    // Verificar cada minuto si ya fue aprobado
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, [checkStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center">
        <div className="text-[#F5A623] text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A1A1A] via-[#2A2A2A] to-[#1A1A1A] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ scale: [1, 1.3, 1], x: [0, 100, 0], y: [0, -50, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-[#fbb040] rounded-full blur-[150px] opacity-20"
        />
        <motion.div
          animate={{ scale: [1, 1.5, 1], x: [0, -100, 0], y: [0, 50, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-[#10B981] rounded-full blur-[120px] opacity-30"
        />
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-32 h-32 mx-auto mb-8 flex items-center justify-center"
          >
            <FileCheck className="w-full h-full text-[#F5A623]" />
          </motion.div>

          <motion.h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6"
            animate={{ color: ['#ffffff', '#fbb040', '#ffffff'] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            DOCUMENTOS EN VERIFICACION
          </motion.h1>

          <p className="text-xl text-white mb-4 font-semibold">
            Estamos verificando tus documentos
          </p>
          <p className="text-base text-[#666666] mb-12">
            El departamento de entrega esta revisando cuidadosamente toda tu informacion
          </p>

          {/* Mensaje estático sin contador */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="glass-card rounded-2xl p-8 mb-8"
            data-testid="verification-message"
          >
            <div className="flex items-center justify-center gap-4 mb-8">
              <Clock className="w-16 h-16 text-[#F5A623]" />
              <div className="text-center">
                <p className="text-[#666666] text-sm mb-2">Tiempo máximo de verificación</p>
                <p className="text-5xl font-black text-[#F5A623]">
                  72 HORAS
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-[#10B981]/20 border border-[#10B981]/30 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
                <p className="text-white font-semibold text-left">Hemos recibido todos tus documentos</p>
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#F5A623]/20 border border-[#F5A623]/30 rounded-lg">
                <Clock className="w-6 h-6 text-[#F5A623]" />
                <p className="text-white font-semibold text-left">Nuestro equipo esta realizando la verificacion</p>
              </div>
              <div className="flex items-center gap-3 p-4 bg-[#3B82F6]/20 border border-[#3B82F6]/30 rounded-lg">
                <FileCheck className="w-6 h-6 text-[#3B82F6]" />
                <p className="text-white font-semibold text-left">Te notificaremos cuando todo este listo (máximo 72 horas)</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            animate={{
              boxShadow: [
                '0 0 40px 20px rgba(245,166,35,0.3)',
                '0 0 80px 40px rgba(255,149,0,0.5)',
                '0 0 40px 20px rgba(245,166,35,0.3)'
              ]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="glass-card rounded-2xl p-8 border-2 border-[#F5A623]"
          >
            <p className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#fbb040] via-yellow-300 to-[#fbb040] mb-4">
              ESTAS MUY CERCA DE ALCANZAR TU META!
            </p>
            <p className="text-white text-base">
              Revisa tu correo electronico. Te hemos enviado toda la informacion.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
